import type { ITableColumn, ITableData, ITableRow } from "@/models/table"
import {
  type IntervalItem,
  type IntervalMeterIdentity,
  meterStackKey,
  toIntervalMeterSource,
} from "@/services/interval"

import { formatIntervalMeterSeriesLabel } from "./format-interval-meter-series-label"

const INTERVAL_CSV_ITEM_COLUMNS = [
  "start_datetime",
  "end_datetime",
  "value",
  "unit",
  "flow_direction",
  "resource_type",
  "provider_name",
] as const

const INTERVAL_CSV_COLUMNS = [
  "site_name",
  "meter",
  "provider_name",
  "resource_type",
  "start_datetime",
  "end_datetime",
  "value",
  "unit",
  "flow_direction",
  "source",
] as const

type IntervalCsvItemColumnId = (typeof INTERVAL_CSV_ITEM_COLUMNS)[number]
type IntervalCsvColumnId = (typeof INTERVAL_CSV_COLUMNS)[number]

type IntervalCsvCell = number | string

interface IntervalCsvRow
  extends ITableRow, Record<IntervalCsvColumnId, IntervalCsvCell> {}

const INTERVAL_CSV_TABLE_COLUMNS: ITableColumn<IntervalCsvRow>[] =
  INTERVAL_CSV_COLUMNS.map((key, order) => ({
    id: order,
    key,
    order,
    title: key,
  }))

type IntervalCsvSource = Pick<
  IntervalItem,
  IntervalCsvItemColumnId | "meter_id"
>

interface IntervalCsvLabelOptions {
  formatFlowDirection: (value: string | null | undefined) => string
  showFlowDirection: boolean
}

const INTERVAL_CSV_ITEM_COLUMN_SET = new Set<string>(INTERVAL_CSV_ITEM_COLUMNS)

const isIntervalCsvItemColumn = (
  column: IntervalCsvColumnId
): column is IntervalCsvItemColumnId => INTERVAL_CSV_ITEM_COLUMN_SET.has(column)

const derivedCsvCell = (
  column: "meter" | "site_name" | "source",
  item: IntervalCsvSource,
  meter: IntervalMeterIdentity | undefined,
  siteName: string,
  options: IntervalCsvLabelOptions
): IntervalCsvCell => {
  if (column === "meter") {
    return formatIntervalMeterSeriesLabel(
      options.formatFlowDirection,
      item.flow_direction,
      meter?.label ?? "",
      options.showFlowDirection
    )
  }
  if (column === "site_name") {
    return siteName
  }

  return toIntervalMeterSource(meter, item.flow_direction)
}

const toCsvRow = (
  item: IntervalCsvSource,
  index: number,
  meter: IntervalMeterIdentity | undefined,
  siteName: string,
  options: IntervalCsvLabelOptions
): IntervalCsvRow => {
  const cells = {} as Record<IntervalCsvColumnId, IntervalCsvCell>
  for (const column of INTERVAL_CSV_COLUMNS) {
    cells[column] = isIntervalCsvItemColumn(column)
      ? (item[column] ?? "")
      : derivedCsvCell(column, item, meter, siteName, options)
  }

  return {
    ...cells,
    id: `${item.meter_id}-${item.start_datetime}-${index}`,
  }
}

export const intervalItemsToCsvTable = (
  items: readonly IntervalCsvSource[],
  meters: readonly IntervalMeterIdentity[],
  siteName: string,
  options: IntervalCsvLabelOptions
): ITableData<IntervalCsvRow> => {
  const metersById = new Map(meters.map((meter) => [meter.id, meter]))

  return {
    columns: INTERVAL_CSV_TABLE_COLUMNS,
    rows: items.map((item, index) =>
      toCsvRow(
        item,
        index,
        metersById.get(meterStackKey(item.meter_id)),
        siteName,
        options
      )
    ),
  }
}

export const buildIntervalExportFileName = (
  title: string,
  siteId: string,
  startIsoDate: string | null | undefined,
  endIsoDate: string | null | undefined,
  // Names the series the file is scoped to, so a filtered download is not
  // mistaken for the full-chart download of the same window.
  seriesLabel?: string | null
): string => {
  const window = `${startIsoDate ?? "unknown"} to ${endIsoDate ?? "unknown"}`
  const base = `${title} ${siteId} ${window}`
  return seriesLabel == null || seriesLabel === ""
    ? base
    : `${base} ${seriesLabel}`
}
